/**
 * The numbers My Files runs on (HS-1441), named once so no component carries a magic value.
 *
 * Product thresholds and limits that the design states are here; what the contract lets the
 * backend change (the quota itself, the `pageSize` cap) arrives in the payload instead.
 */
import type { FileStatusDto } from './files.types';

/** Ten rows per page, as the design's pager draws (fourteen files over two pages). */
export const FILES_PAGE_SIZE = 10;

/** The two columns the endpoint will order by (contract §2); the design draws newest first. */
export const SORT_FIELDS = { name: 'name', createdAt: 'createdAt' } as const;
export type FilesSortField = (typeof SORT_FIELDS)[keyof typeof SORT_FIELDS];
export const DEFAULT_SORT_FIELD: FilesSortField = SORT_FIELDS.createdAt;
export const DEFAULT_SORT = `${DEFAULT_SORT_FIELD}:desc`;

/**
 * The status values `GET /api/portal/me/files` accepts (HS-1484).
 *
 * **Deliberately shorter than `FileStatusDto`.** The view model knows twelve statuses; the API's
 * `PortalFileStatus` enumeration has these eight, and a value outside it is a **400**, not an empty
 * result. So the filter may only offer what the endpoint can be asked about, while the chips and
 * their EN/FR copy keep the full twelve — `blocked`, `syncFailed`, `credentialLost` and
 * `transferFailed` are Cloud Storage's, and deleting them would only mean writing them again.
 *
 * Declared here rather than in the two modules that need it: it lived in three places, drifted from
 * the API in all three, and one list is what stops that happening twice.
 *
 * Six of the eight match nothing until cloud storage exists. They are offered anyway (sweep
 * decision, 2026-09-14) because the enumeration is the contract, and narrowing to the two that can
 * match today would have to be widened back the day the rest become reachable.
 */
export const FILTERABLE_STATUSES: FileStatusDto[] = [
  'synced',
  'unsynced',
  'noCredential',
  'conflict',
  'notFound',
  'legalHold',
  'quarantined',
  'cached',
];

/** The six fields the filter builder offers (sweep decision, 2026-09-08). */
export const FILE_FILTER_FIELDS = {
  type: 'type',
  origin: 'origin',
  located: 'located',
  date: 'date',
  size: 'size',
  status: 'status',
} as const;
export type FileFilterFieldId = (typeof FILE_FILTER_FIELDS)[keyof typeof FILE_FILTER_FIELDS];

/** The Origin field's one non-lab choice: files with no session behind them. */
export const ORIGIN_NONE_OPTION = 'none';

/** Sizes are typed in megabytes and sent in bytes (contract §2). */
export const BYTES_PER_MB = 1_048_576;

/** The design's "Max 500 MB per file". Checked client-side before a byte is sent (contract §8). */
export const MAX_UPLOAD_BYTES = 524_288_000;

/**
 * What one download or one bulk delete may carry (HS-1484).
 *
 * The server refuses a larger selection with `409 selectionTooLarge` before it builds anything, so
 * these are mirrored here to refuse it earlier — while the user is still looking at the banner
 * rather than at a failed toast. The byte cap can only be checked for a selection made of ids: a
 * "select all N" is resolved server-side when the action runs, and the page holds ten rows, not N.
 */
export const MAX_SELECTION_FILES = 500;
/** 5 GiB, written out for the same reason {@link BYTES_PER_MB} is. */
export const MAX_SELECTION_BYTES = 5_368_709_120;

/**
 * How long a download fetch may stay silent before the toast reports "Unknown outcome — checking…"
 * and offers Check status (contract §6). Generous, because a large archive over a slow link is
 * silent for a while without being lost.
 */
export const DOWNLOAD_UNKNOWN_AFTER_MS = 60_000;

/**
 * The largest text file the preview page renders inline (HS-1441A). Above it the page offers the
 * download instead: a `<pre>` of several megabytes is slower to lay out than to save.
 */
export const PREVIEW_TEXT_MAX_BYTES = 1_048_576;

/**
 * The largest CSV, `.xls` or `.ods` the preview parses (HS-1474). The parsers run on the main
 * thread and read the whole file to count its rows, so past this the page offers the download.
 * `.xlsx` keeps no cap, as it had none before.
 */
export const PREVIEW_SHEET_MAX_BYTES = 20_971_520;

/**
 * The largest TIFF the preview decodes, in pixels (HS-1474): the decoder hands back four bytes a
 * pixel, so 40 megapixels is 160 MB of memory before the canvas copies it.
 */
export const PREVIEW_TIFF_MAX_PIXELS = 40_000_000;

/** How many PDF pages the preview draws before it stops and says so; the download has the rest. */
export const PREVIEW_PDF_MAX_PAGES = 30;

/** How many spreadsheet rows the preview lists per sheet before it stops and says so. */
export const PREVIEW_SHEET_MAX_ROWS = 500;

/** The `?provider=…&result=…` pair the OAuth return carries (contract §9). */
export const CLOUD_RETURN_PARAMS = { provider: 'provider', result: 'result' } as const;

/**
 * The server's folder-name rule (HS-1724), mirrored so the dialog refuses before it sends: the name
 * is trimmed, and then must be 1 to 255 characters — the width of the column it is stored in.
 * Nothing else is checked, because the server checks nothing else; an invented rule here would
 * refuse a name the hood accepts.
 */
export const FOLDER_NAME_MAX_LENGTH = 255;

/** What a folder's full path is spelled with in the move picker — "Reports / 2026 / March". */
export const FOLDER_PATH_SEPARATOR = ' / ';
